import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Check, CheckCheck, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Payment-help chatbot — floating bottom-left widget shown on the /7days and
 * /nabd checkout pages. Visitor stuck on Stripe? Tap the green bubble, pick
 * the matching FAQ, and the bot mints a FRESH Stripe Checkout Session and
 * redirects straight to it.
 *
 * Props
 *   funnel    "seven" | "nabd"      Decides which /api/{funnel}/checkout to call.
 *   bundleKey string (seven only)   Bundle to re-create — caller passes the
 *                                   currently-selected bundle from the page.
 *   getPayload () => extra payload  Optional. Lets the host page inject the
 *                                   current email so the new Stripe session
 *                                   is pre-filled identically to the one the
 *                                   visitor abandoned.
 *
 * Analytics
 *   Reuses the existing chatbot_open / chatbot_message / chatbot_cta_click
 *   events so admin KPIs (already plumbed for both funnels) reflect the
 *   rescue-widget engagement without any new schema.
 */

const PRIMARY = "#25D366";       // WhatsApp green
const PRIMARY_DARK = "#128C7E";
const HEADER_BG = "#075E54";
const FONT_AR = "'Tajawal','IBM Plex Sans Arabic',-apple-system,system-ui,sans-serif";

const sendTrack = (funnel, event, props = {}) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/${funnel}/track`;
    const visitor_id = localStorage.getItem(funnel === "nabd" ? "nabd_vid" : "seven_vid") || "anon";
    const body = JSON.stringify({ event, props, visitor_id, page: `checkout-${funnel}-chatbot` });
    if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    else api.post(`/${funnel}/track`, JSON.parse(body));
  } catch (_) { /* analytics never breaks UX */ }
};

// Scripted Arabic flow — feminine-singular for seven, neutral for nabd.
// All paths converge on the same "ابدئي/ابدأ الدفع الآن" CTA which mints
// a fresh session.
const buildFlow = (funnel) => {
  const isSeven = funnel === "seven";
  const greet = isSeven
    ? "هلا فيكِ 🤍 وش المشكلة اللي صادفتكِ في الدفع؟ اختاري من الخيارات تحت."
    : "هلا فيك 🤍 وش المشكلة اللي صادفتك في الدفع؟ اختر من الخيارات تحت.";
  const ctaLabel = isSeven ? "افتحي صفحة الدفع الآن" : "افتح صفحة الدفع الآن";
  return {
    start: {
      bot: greet,
      replies: [
        "البطاقة ما تشتغل",
        "ما وصلني رابط الدفع",
        "وش طرق الدفع المتاحة؟",
        "أبغى رابط دفع جديد",
        ctaLabel,
      ],
    },
    "البطاقة ما تشتغل": {
      bot: isSeven
        ? "جربي بطاقة ثانية، أو استخدمي Apple Pay مباشرة من الجوال 🙌\nصفحة الدفع تدعم Visa و Mastercard و Apple Pay و Google Pay. لو ما زال ما يعمل، نرسل لكِ رابط جديد:"
        : "جرّب بطاقة ثانية، أو استخدم Apple Pay مباشرة من الجوال 🙌\nصفحة الدفع تدعم Visa و Mastercard و Apple Pay و Google Pay. إذا استمرت المشكلة، نرسل لك رابط جديد:",
      replies: [ctaLabel],
    },
    "ما وصلني رابط الدفع": {
      bot: isSeven
        ? "ما يحتاج رابط على بريدكِ — صفحة الدفع تفتح هنا مباشرة. اضغطي تحت ونحوّلكِ:"
        : "ما يحتاج رابط على بريدك — صفحة الدفع تفتح هنا مباشرة. اضغط تحت ونحوّلك:",
      replies: [ctaLabel],
    },
    "وش طرق الدفع المتاحة؟": {
      bot: "متاح الدفع عبر:\n• Apple Pay (بنقرة واحدة من الآيفون)\n• Google Pay (للأندرويد)\n• بطاقات Visa و Mastercard\n\nالدفع آمن عبر Stripe، وتم اعتماد ضمان استرداد ٧ أيام.",
      replies: [ctaLabel],
    },
    "أبغى رابط دفع جديد": {
      bot: isSeven
        ? "تمام 🤍 رح أفتح لكِ صفحة دفع جديدة الآن."
        : "تمام 🤍 رح أفتح لك صفحة دفع جديدة الآن.",
      replies: [ctaLabel],
    },
  };
};

const Bubble = ({ from, text, time }) => (
  <div className={`flex ${from === "bot" ? "justify-start" : "justify-end"} px-3`}>
    <div
      className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13.5px] leading-[1.7] shadow-sm whitespace-pre-line ${
        from === "bot"
          ? "bg-white text-[#111B21] rounded-tr-2xl rounded-tl-sm"
          : "bg-[#D9FDD3] text-[#111B21] rounded-tl-2xl rounded-tr-sm"
      }`}
      dir="rtl"
      style={{ fontFamily: FONT_AR }}
    >
      {text}
      <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-[#667781]">
        <span>{time}</span>
        {from === "user" && <CheckCheck className="w-3 h-3 text-[#53BDEB]" />}
        {from === "bot" && <Check className="w-3 h-3 text-[#667781]" />}
      </div>
    </div>
  </div>
);

export default function PaymentHelpChatbot({ funnel, bundleKey, getPayload, open: openProp, onClose }) {
  const [openInternal, setOpenInternal] = useState(false);
  // Controlled when `openProp` is provided (parent drives via trust-block CTA);
  // uncontrolled otherwise (the floating FAB toggles its own state).
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : openInternal;
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [typing, setTyping] = useState(false);
  const [working, setWorking] = useState(false);
  const scrollerRef = useRef(null);

  const flow = buildFlow(funnel);
  const ctaLabel = funnel === "seven" ? "افتحي صفحة الدفع الآن" : "افتح صفحة الدفع الآن";
  const nowTime = () => new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, typing]);

  const pushBot = (text, qr = []) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((prev) => [...prev, { from: "bot", text, time: nowTime() }]);
      setQuickReplies(qr);
    }, 600);
  };

  const handleOpen = () => {
    if (open) return;
    if (isControlled) {
      // Parent is the source of truth — let it know via a no-op effect path.
      // (the FAB only renders when not controlled, so this branch is rare)
    } else {
      setOpenInternal(true);
    }
    sendTrack(funnel, "chatbot_open", { source: "checkout-rescue" });
    if (messages.length === 0) pushBot(flow.start.bot, flow.start.replies);
  };

  // First-open greeting also triggers when the parent flips openProp to true.
  useEffect(() => {
    if (open && messages.length === 0) {
      sendTrack(funnel, "chatbot_open", { source: "checkout-rescue" });
      pushBot(flow.start.bot, flow.start.replies);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (isControlled && typeof onClose === "function") onClose();
    else setOpenInternal(false);
  };

  const mintAndRedirect = async () => {
    if (working) return;
    setWorking(true);
    sendTrack(funnel, "chatbot_cta_click", { source: "checkout-rescue", label: ctaLabel });
    try {
      const extra = (typeof getPayload === "function" ? getPayload() : {}) || {};
      const payload = funnel === "seven"
        ? { bundle: bundleKey || "complete", ...extra }
        : { ...extra };
      const { data } = await api.post(`/${funnel}/checkout`, payload);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      pushBot("صار خلل بسيط — حاولي مرة ثانية بعد لحظات 🙏", [ctaLabel]);
    } catch (_) {
      pushBot("ما قدرنا نفتح صفحة دفع جديدة الآن — جربي بعد دقيقة 🙏", [ctaLabel]);
    } finally {
      setWorking(false);
    }
  };

  const handleReply = (label) => {
    setQuickReplies([]);
    setMessages((prev) => [...prev, { from: "user", text: label, time: nowTime() }]);
    sendTrack(funnel, "chatbot_message", { reply: label });
    if (label.includes("صفحة الدفع")) return mintAndRedirect();
    const node = flow[label];
    if (node) pushBot(node.bot, node.replies);
    else pushBot("اختاري من الخيارات تحت 🤍", flow.start.replies);
  };

  return (
    <>
      {/* Floating FAB only when the parent isn't controlling open-state.
          On the checkout pages the trust-block CTA drives the bot, so the
          FAB is suppressed to avoid two entry points competing visually. */}
      {!open && !isControlled && (
        <button
          onClick={handleOpen}
          aria-label="افتحي محادثة الدعم"
          data-testid={`pay-help-fab-${funnel}`}
          className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-[60] group"
        >
          <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ background: PRIMARY }} />
          <div
            className="relative flex items-center justify-center w-[52px] h-[52px] rounded-full shadow-[0_12px_28px_-8px_rgba(37,211,102,0.55)] transition-transform group-hover:scale-[1.05]"
            style={{ background: `linear-gradient(180deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}
          >
            <MessageCircle className="w-6 h-6 text-white" strokeWidth={2.2} fill="white" />
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] border-2 border-white text-white text-[9.5px] font-extrabold flex items-center justify-center">1</span>
          </div>
        </button>
      )}

      {open && (
        <div
          dir="rtl"
          lang="ar"
          data-testid={`pay-help-window-${funnel}`}
          className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-[60] w-[calc(100vw-32px)] sm:w-[360px] max-w-[380px] h-[70vh] sm:h-[540px] rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] flex flex-col border border-black/10"
          style={{ background: "#ECE5DD", fontFamily: FONT_AR }}
        >
          <div className="flex items-center gap-3 px-3.5 py-2.5" style={{ background: HEADER_BG }}>
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center border border-white/20">
              <MessageCircle className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1 leading-tight">
              <div className="text-white text-[14px] font-bold">دعم الدفع</div>
              <div className="text-[#B9D9D2] text-[11px] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] inline-block" />
                متصلون الآن
              </div>
            </div>
            <button
              onClick={handleClose}
              aria-label="إغلاق"
              data-testid={`pay-help-close-${funnel}`}
              className="text-white/80 hover:text-white p-1.5"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto py-3 space-y-2"
            style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"40\" height=\"40\"><circle cx=\"20\" cy=\"20\" r=\"0.6\" fill=\"%23000\" opacity=\"0.04\"/></svg>')" }}
          >
            {messages.map((m, i) => <Bubble key={i} from={m.from} text={m.text} time={m.time} />)}
            {typing && (
              <div className="flex justify-start px-3">
                <div className="bg-white rounded-2xl rounded-tr-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#667781] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#667781] animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#667781] animate-bounce" style={{ animationDelay: "240ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {!typing && quickReplies.length > 0 && (
            <div className="px-3 py-2.5 bg-[#ECE5DD] border-t border-black/[0.06] space-y-1.5" data-testid={`pay-help-qrs-${funnel}`}>
              {quickReplies.map((qr, i) => {
                const isCta = qr.includes("صفحة الدفع");
                return (
                  <button
                    key={i}
                    onClick={() => handleReply(qr)}
                    disabled={working && isCta}
                    data-testid={`pay-help-qr-${funnel}-${i}`}
                    className={`w-full text-right text-[13px] font-bold px-3.5 py-2.5 rounded-full transition-colors border disabled:opacity-70 ${
                      isCta
                        ? "bg-gradient-to-b from-[#25D366] to-[#128C7E] text-white border-transparent hover:opacity-95 shadow-[0_6px_20px_-6px_rgba(37,211,102,0.55)] flex items-center justify-center gap-2"
                        : "bg-white text-[#075E54] border-[#075E54]/15 hover:bg-[#075E54]/[0.04]"
                    }`}
                  >
                    {working && isCta ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isCta && <ArrowLeft className="w-3.5 h-3.5" />)}
                    {qr}
                  </button>
                );
              })}
            </div>
          )}

          <div className="px-3 py-2 bg-[#F0F2F5] text-center">
            <div className="text-[10.5px] text-[#667781]">رد آلي تلقائي — متاح ٢٤/٧</div>
          </div>
        </div>
      )}
    </>
  );
}
